// CSRF Token utility functions
import Cookies from 'universal-cookie';

const cookies = new Cookies();

export const getCsrfTokenKeyByEnv = () => {
  const appEnv = (process.env.REACT_APP_ENV || '').toLowerCase();

  if (appEnv === 'qa') return 'QA-X-CSRF-TOKEN';
  if (appEnv === 'uat') return 'UAT-X-CSRF-TOKEN';
  if (appEnv === 'dev') return 'DEV-X-CSRF-TOKEN';

  // Keep prod and all other environments on the default key.
  return 'X-CSRF-TOKEN';
};

// Global variable to track refresh interval
let csrfRefreshInterval = null;

/**
 * Extract CSRF token from response headers and store in cookies
 */
export const extractAndStoreCsrfToken = (response) => {
  try {
    const csrfTokenKey = getCsrfTokenKeyByEnv();
    // First try to get from response headers
    if (response && response.headers) {
      const headerToken = response.headers.get('x-csrf-token') ||
                         response.headers.get('X-CSRF-TOKEN');
      if (headerToken) {
        cookies.set(csrfTokenKey, headerToken, { path: '/' });
        return headerToken;
      }
    }
    
    // Fallback: check if token already exists in cookies
    return cookies.get(csrfTokenKey) || null;
  } catch (error) {
    console.error('Error extracting CSRF token:', error);
  }
  return null;
};

/**
 * Check if CSRF token is expired
 * Token expiry is managed by the server via cookie attributes — always return false.
 */
export const isCsrfTokenExpired = () => false;

/**
 * Get CSRF token from cookies
 */
export const getCsrfToken = async () => {
  return cookies.get(getCsrfTokenKeyByEnv()) || '';
};

/**
 * Synchronous version of getCsrfToken for immediate use
 */
export const getCsrfTokenSync = () => {
  return cookies.get(getCsrfTokenKeyByEnv()) || '';
};

/**
 * Fetch a new CSRF token — returns current cookie value.
 * Tokens are issued by the server via response headers and captured by extractAndStoreCsrfToken().
 */
export const fetchNewCsrfToken = async () => {
  return cookies.get(getCsrfTokenKeyByEnv()) || null;
};

/**
 * Start/Stop periodic CSRF token refresh — no-op stubs.
 * Token expiry is managed by the server via cookie attributes.
 */
export const startCsrfTokenRefresh = () => {};
export const stopCsrfTokenRefresh = () => {
  if (csrfRefreshInterval) {
    clearInterval(csrfRefreshInterval);
    csrfRefreshInterval = null;
  }
};

/**
 * Set CSRF token in cookies
 */
export const setCsrfToken = (token) => {
  cookies.set(getCsrfTokenKeyByEnv(), token, { path: '/' });
};

/**
 * Clear CSRF token from cookies
 */
export const clearCsrfToken = () => {
  cookies.remove(getCsrfTokenKeyByEnv(), { path: '/' });
};

/**
 * Ensure CSRF token is available
 */
export const ensureCsrfToken = async () => {
  return cookies.get(getCsrfTokenKeyByEnv()) || '';
};

/**
 * Handle CSRF token issues during API calls
 */
export const handleCsrfTokenInconsistency = async () => {
  return cookies.get(getCsrfTokenKeyByEnv()) || null;
};

/**
 * Get headers with CSRF token (async version)
 */
export const getHeadersWithCsrf = async (additionalHeaders = {}) => {
  const csrfTokenKey = getCsrfTokenKeyByEnv();
  const csrfToken = cookies.get(csrfTokenKey);
  const headers = { ...additionalHeaders };
  if (csrfToken) headers[csrfTokenKey] = csrfToken;
  return headers;
};

/**
 * Get headers with CSRF token (sync version)
 */
export const getHeadersWithCsrfSync = (additionalHeaders = {}) => {
  const csrfTokenKey = getCsrfTokenKeyByEnv();
  const csrfToken = cookies.get(csrfTokenKey);
  const headers = { ...additionalHeaders };
  if (csrfToken) headers[csrfTokenKey] = csrfToken;
  return headers;
};